import { useState, useMemo } from 'react';
import { Box, Alert } from '@mui/material';
import { DialogMode } from '../../type/DialogMode';
import { PageTitle } from '../../components/PageTitle';
import { AnimatedBox } from '../../components/AnimatedBox';
import { EmptyState } from '../../components/EmptyState';
import { LoadingState } from '../../components/LoadingState';
import { EnterpriseDialog } from '../../components/EnteriseDialog';
import { AddEnterpriseButton } from '../../components/AddEnterpriseButton';
import { EnterpriseSummaryTable } from '../../components/EnteriseSummaryTable';
import { EnterpriseTable } from '../../components/EnteriseTable';
import { Enterprise } from '../../type';

interface EnterpriseContentProps {
  enterprises: Enterprise[];
  loading: boolean;
  error: string | null;
  addEnterprise: (enterprise: Enterprise) => Promise<void>;
  updateEnterprise: (enterprise: Enterprise) => Promise<void>;
  deleteEnterprise: (enterpriseId: string) => Promise<void>;
}

const EnterpriseContent = ({
  enterprises,
  loading,
  error,
  addEnterprise,
  updateEnterprise,
  deleteEnterprise,
}: EnterpriseContentProps) => {  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEnterprise, setSelectedEnterprise] = useState<Enterprise | null>(null);
  const [dialogMode, setDialogMode] = useState<DialogMode>(DialogMode.CREATE);

  // 기업 통계 계산
  const companyStats = useMemo(() => {
    return enterprises.reduce(
      (acc) => {
        acc.total += 1;
        return acc;
      },
      { total: 0 }
    );
  }, [enterprises]);

  const handleSubmit = async (enterpriseData: Enterprise): Promise<boolean> => {
    try {
      if (dialogMode === DialogMode.CREATE) {
        await addEnterprise(enterpriseData);
      } else {
        await updateEnterprise(enterpriseData);
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleDelete = async (enterpriseId: string) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      await deleteEnterprise(enterpriseId);
    }
  };

  const handleAddCompany = () => {
    setSelectedEnterprise(null);
    setDialogMode(DialogMode.CREATE);
    setDialogOpen(true);
  };

  const handleEditCompany = (enterprise: Enterprise) => {
    setSelectedEnterprise(enterprise);
    setDialogMode(DialogMode.EDIT);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedEnterprise(null);
  };

  if (loading) {
    return (
      <AnimatedBox>
        <PageTitle>기업 관리</PageTitle>
        <LoadingState />
      </AnimatedBox>
    );
  }

  return (
    <AnimatedBox>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3 
      }}>
        <PageTitle>기업 관리</PageTitle>
        <AddEnterpriseButton onAddClick={handleAddCompany} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {enterprises.length === 0 ? (
        <EmptyState message="아직 등록된 기업이 없습니다." />
      ) : (
        <Box sx={{
          display: 'flex',
          gap: 2,
        }}>
          <EnterpriseSummaryTable
            totalEnterprises={companyStats.total}
          />
          <EnterpriseTable
            data={enterprises}
            onEnterpriseSelect={handleEditCompany}
            onDelete={handleDelete}
          />
        </Box>
      )}

      <EnterpriseDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleSubmit}
        enterprise={selectedEnterprise}
        mode={dialogMode}
      />
    </AnimatedBox>
  );
};

export default EnterpriseContent; 